// JSON API client. Every request carries X-Requested-With (the server's CSRF guard).
export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) { super(message); this.status = status; }
}

export async function api<T = any>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method, credentials: 'same-origin',
    headers: { 'content-type': 'application/json', 'x-requested-with': 'sheet' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, json.error ?? res.statusText);
  return json as T;
}

/** `username` is the LDAP uid the SSO gave; `roles` its groups as of the last check. */
export interface Me { id: number; username: string; email: string | null; displayName: string | null; roles?: string[] }
export interface DocSummary { id: string; title: string; updated_at: string; role: string; owner_name: string; folder_id?: string | null }
export interface Member { id: number; username: string; email: string | null; display_name: string | null; role: string }
export interface Comment { id: number; sheet_id: number | null; r: number | null; c: number | null; parent_id: number | null; body: string; resolved: boolean; created_at: string; author: string; author_id: number }

/** When the sign-in ends (set by me()). */
export let sessionExpiresAt: string | null = null;
/** The sign-out link for this sign-in (set by me()): it carries a token bound to the session. */
let logoutUrl: string | null = null;
export const me = async (): Promise<Me | null> => {
  try {
    const r = await api<{ user: Me; sessionExpiresAt: string | null; logoutUrl?: string }>('GET', '/api/me');
    sessionExpiresAt = r.sessionExpiresAt ?? null; logoutUrl = r.logoutUrl ?? null;
    return r.user;
  } catch (e) { if (e instanceof ApiError && e.status === 401) return null; throw e; }
};
/** To the SSO's sign-in page, coming back to `returnTo` (this page by default) afterwards. A navigation, not a fetch. */
export function goLogin(returnTo = location.pathname + location.search + location.hash): void {
  location.assign('/login?return=' + encodeURIComponent(returnTo));
}
/** Sign out: the server ends the session, then sends the browser through the SSO's sign-out and back home. */
export function logout(): void {
  location.assign(logoutUrl ?? '/');
}
